import Add from "@mui/icons-material/Add";
import DataObject from "@mui/icons-material/DataObject";
import {
	Divider,
	FormControl,
	InputLabel,
	ListSubheader,
	MenuItem,
	Select,
	Stack,
	Typography
} from "@mui/material";
import { useMemo, useState } from "react";

import type { Option } from "../constants";
import { canvasSelectSx } from "../forms/styled/CanvasFormTextField.styled";
import type { RuleVariable, SimpleVariableInput } from "../hooks/useRuleVariables";
import type { SimpleVariableModalProps, SimpleVariableModalState } from "../modals/SimpleVariableModal";
import SimpleVariableModal from "../modals/SimpleVariableModal";
import type { VariableGroupV2 } from "./form-entity-select";

export interface SimpleVariableSelectProps {
	value: string;
	onChange: (value: string) => void;
	simpleVariables: RuleVariable[];
	/** Prefer full rule variable list (e.g. from `allVariables`) so names never collide with formula or other types. */
	existingVariables?: RuleVariable[];
	/** When `existingVariables` is omitted, merged with `simpleVariables` for duplicate-name checks (e.g. formula names). */
	formulaVariables?: RuleVariable[];
	onAddSimpleVariable: (input: SimpleVariableInput) => RuleVariable | null;
	fullWidth?: boolean;
	/** Passed through to `SimpleVariableModal` so the value picker matches `SimpleVariableForm`. */
	platformId?: SimpleVariableModalProps["platformId"];
	resourceVariableGroups?: VariableGroupV2[];
	formulaVariableOptions?: Option[];
	resourceScreenFields?: Option[];
	globalConstantOptions?: Option[];
	ruleFormulaVariables?: RuleVariable[];
	ruleDatabaseSingleRowVariables?: RuleVariable[];
	ruleDatabaseMultiRowVariables?: RuleVariable[];
}

const ADD_SIMPLE_VARIABLE_VALUE = "__add_simple_variable__";

const SimpleVariableSelect = (props: SimpleVariableSelectProps) => {
	const {
		value,
		onChange,
		simpleVariables,
		existingVariables,
		formulaVariables = [],
		onAddSimpleVariable,
		fullWidth = true,
		platformId,
		resourceVariableGroups,
		formulaVariableOptions,
		resourceScreenFields,
		globalConstantOptions,
		ruleFormulaVariables,
		ruleDatabaseSingleRowVariables,
		ruleDatabaseMultiRowVariables,
	} = props;

	const variablesForNaming = useMemo(
		() => existingVariables ?? [...simpleVariables, ...formulaVariables],
		[existingVariables, formulaVariables, simpleVariables],
	);

	const [selectOpen, setSelectOpen] = useState(false);
	const [modalOpen, setModalOpen] = useState(false);

	const handleSelectChange = (newValue: string) => {
		if (newValue === ADD_SIMPLE_VARIABLE_VALUE) {
			return;
		}
		onChange(newValue);
	};

	const handleAddClick = (event: React.MouseEvent) => {
		event.stopPropagation();
		setSelectOpen(false);
		setModalOpen(true);
	};

	const handleModalSave = (state: SimpleVariableModalState & { dataTypeId: number }) => {
		const created = onAddSimpleVariable({
			name: state.name,
			dataTypeId: state.dataTypeId,
			value: state.value ?? "",
			valueLiteral: state.valueLiteral,
			valueCategory: state.valueCategory,
			valueDatabaseTableFieldId: state.valueDatabaseTableFieldId,
			valueScreenFieldId: state.valueScreenFieldId,
			valueDatabaseTableFieldName: state.valueDatabaseTableFieldName,
			valueScreenFieldName: state.valueScreenFieldName,
			valueSimpleVariableName: state.valueSimpleVariableName,
			valueSimpleVariableId: state.valueSimpleVariableId,
			valueFormulaVariableId: state.valueFormulaVariableId,
			valueFormulaVariableName: state.valueFormulaVariableName,
			valueDatabaseSingleRowVariableId: state.valueDatabaseSingleRowVariableId,
			valueDatabaseSingleRowVariableName: state.valueDatabaseSingleRowVariableName,
			valueGlobalConstant: state.valueGlobalConstant,
			valueGlobalConstantName: state.valueGlobalConstantName,
		});
		if (created) {
			setModalOpen(false);
		}
	};

	return (
		<>
			<FormControl
				fullWidth={fullWidth}
				sx={{
					...canvasSelectSx,
					minWidth: 0,
					...(fullWidth ? { flex: "1 1 0", width: "auto" } : {}),
					"& .MuiOutlinedInput-root": {
						minWidth: 0,
						overflow: "hidden",
					},
					"& .MuiSelect-select": {
						whiteSpace: "nowrap",
						textOverflow: "ellipsis",
						overflow: "hidden",
						display: "block",
						minWidth: 0,
						maxWidth: "100%",
						paddingRight: "32px",
					},
				}}
			>
				<InputLabel>Simple Variable</InputLabel>
				<Select
					value={value}
					label="Simple Variable"
					open={selectOpen}
					onOpen={() => setSelectOpen(true)}
					onClose={() => setSelectOpen(false)}
					onChange={(event) => handleSelectChange(event.target.value)}
					sx={{ minWidth: 0, maxWidth: "100%", width: "100%" }}
				>
					<ListSubheader disableSticky>
						<Stack direction="row" alignItems="center" spacing={0.75} sx={{ py: 0.5 }}>
							<DataObject fontSize="small" sx={{ color: "#D48200" }} />
							<Typography variant="caption" fontWeight={600} color="text.secondary">
								Simple Variables
							</Typography>
						</Stack>
					</ListSubheader>

					{simpleVariables.map((variable) => (
						<MenuItem key={variable.id} value={variable.name}>
							{variable.name}
						</MenuItem>
					))}

					<Divider />

					<MenuItem
						value={ADD_SIMPLE_VARIABLE_VALUE}
						onClick={handleAddClick}
						sx={{ color: "primary.main" }}
					>
						<Stack direction="row" alignItems="center" spacing={1}>
							<Add fontSize="small" />
							<Typography variant="body2" color="primary">
								Add Simple Variable
							</Typography>
						</Stack>
					</MenuItem>
				</Select>
			</FormControl>

			<SimpleVariableModal
				open={modalOpen}
				onClose={() => setModalOpen(false)}
				onSave={handleModalSave}
				existingVariables={variablesForNaming}
				platformId={platformId}
				resourceVariableGroups={resourceVariableGroups}
				formulaVariableOptions={formulaVariableOptions}
				resourceScreenFields={resourceScreenFields}
				globalConstantOptions={globalConstantOptions}
				ruleSimpleVariables={simpleVariables}
				ruleFormulaVariables={ruleFormulaVariables ?? formulaVariables}
				ruleDatabaseSingleRowVariables={ruleDatabaseSingleRowVariables}
				ruleDatabaseMultiRowVariables={ruleDatabaseMultiRowVariables}
			/>
		</>
	);
};

export default SimpleVariableSelect;
